import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import Text from "./shared/Text";

/**
 * DarkPricingCompareSection — full-bleed dark section comparing the
 * competitor's pricing tiers to Graphy's.
 *
 * Layout: centered title ("Compare <Competitor> Pricing & Graphy Pricing"
 * — competitor name in muted grey) · primary CTA · two side-by-side
 * pricing cards (left = competitor on dark grey surface; right = Graphy
 * on white with pink/orange glow).
 */

export type PricingTier = {
  name: string;
  price: string;
  savings?: string;
};

export type DarkPricingCompareData = {
  titleIntro: string;
  competitorName: string;
  titleOutro: string;
  primaryCta: CtaType;
  competitor: {
    logoSrc?: string;
    name: string;
    tiers: PricingTier[];
  };
  graphy: {
    tiers: PricingTier[];
  };
};

const DarkPricingCompareSection = ({
  titleIntro,
  competitorName,
  titleOutro,
  primaryCta,
  competitor,
  graphy,
}: DarkPricingCompareData) => {
  return (
    <section className="relative w-full overflow-hidden bg-neutralBlack">
      {/* Radial glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 60% at 50% 100%, rgba(255, 110, 170, 0.18) 0%, rgba(0, 0, 0, 0) 65%)",
        }}
      />

      <div className="container relative z-10 mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-6xl">
        <Text
          as="h2"
          variant="display-lg"
          className="mx-auto max-w-4xl text-center text-[28px] sm:text-[32px] md:text-[40px] lg:text-[48px]"
        >
          <span className="text-white">{titleIntro} </span>
          <span className="text-white">{competitorName} </span>
          <span className="text-white">{titleOutro}</span>
        </Text>
        <div className="mt-8 flex items-center justify-center md:mt-10">
          <CtaButtonGroup
            ctas={[primaryCta]}
            gap="flex gap-3"
            fullWidthOnMobile={false}
          />
        </div>

        <div className="mt-12 grid grid-cols-1 items-stretch gap-6 md:mt-16 md:grid-cols-2 md:gap-8">
          {/* Competitor card */}
          <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm md:p-8">
            <div className="flex items-center justify-center pb-4">
              {competitor.logoSrc ? (
                <span className="inline-flex items-center rounded-xl bg-white px-4 py-2">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={competitor.logoSrc} alt={competitor.name} className="h-5 object-contain md:h-6" />
                </span>
              ) : (
                <Text
                  as="div"
                  variant="title-lg"
                  className="text-[22px] font-semibold uppercase tracking-wide text-white/70"
                >
                  {competitor.name}
                </Text>
              )}
            </div>
            <div className="border-t border-white/10" />
            <div className="mt-4 flex flex-1 flex-col gap-4">
              {competitor.tiers.map((t) => (
                <div
                  key={t.name}
                  className="flex items-center justify-between gap-4 rounded-xl bg-white/[0.03] px-4 py-4"
                >
                  <Text
                    as="div"
                    variant="body-lg"
                    className="text-[15px] font-medium text-white"
                  >
                    {t.name}
                  </Text>
                  <Text
                    as="div"
                    variant="title-md"
                    className="text-[18px] font-semibold text-white"
                  >
                    {t.price}
                  </Text>
                </div>
              ))}
            </div>
          </div>

          {/* Graphy card */}
          <div className="relative h-full">
            <div
              aria-hidden="true"
              className="absolute -inset-2 rounded-[28px] bg-gradient-to-br from-[#FFBAD5] to-[#FF6A3D] opacity-60 blur-2xl"
            />
            <div className="relative flex h-full flex-col rounded-3xl bg-white p-6 md:p-8">
              <div className="flex items-center justify-center pb-4">
                <img
                  src="/images/graphy_logo_black.svg"
                  alt="Graphy"
                  className="h-6 md:h-7"
                />
              </div>
              <div className="border-t border-neutralBlack/10" />
              <div className="mt-4 flex flex-1 flex-col gap-4">
                {graphy.tiers.map((t) => (
                  <div
                    key={t.name}
                    className="flex items-center justify-between gap-4 rounded-xl bg-slateLight px-4 py-4"
                  >
                    <Text
                      as="div"
                      variant="body-lg"
                      className="text-[15px] font-medium text-neutralBlack"
                    >
                      {t.name}
                    </Text>
                    <div className="flex items-center gap-3">
                      <Text
                        as="div"
                        variant="title-md"
                        className="text-[18px] font-semibold text-neutralBlack"
                      >
                        {t.price}
                      </Text>
                      {t.savings ? (
                        <span className="rounded-full bg-[#CCFF00] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutralBlack">
                          {t.savings} savings
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default DarkPricingCompareSection;
